import { Episode } from "../api";
export type Language = "original" | "en" | "fr" | "ar" | "ja";
import { Appearance as ReaderAppearance, Theme } from "./preferences";
export type { Theme } from "./preferences";
export type Preferences = {
  language?: Language;
  theme?: Theme;
  appearance?: Partial<ReaderAppearance>;
};
export type User = {
  id: string;
  username: string;
  language: Language;
  theme: Theme;
  appearance: ReaderAppearance;
};
export type AppEpisode = Episode & {
  show_id: string;
  imported_at: string;
  metadata: Episode["metadata"] & { guests?: string[]; format?: string };
};
export type Show = {
  id: string;
  name: string;
  station_id: string;
  station_name: string;
  episode_ids: string[];
  languages: string[];
};
export type Station = {
  id: string;
  name: string;
  country: string;
  artwork_url?: string | null;
  episode_ids: string[];
  show_ids: string[];
};
export type Appearance = {
  episode_id: string;
  role: string;
  status: string;
  start_ms: number | null;
  title: string | null;
  track_id?: string;
};
export type Artist = {
  id: string;
  name: string;
  appearances: Appearance[];
  episode_ids: string[];
};
export type Catalogue = {
  episodes: AppEpisode[];
  shows: Show[];
  stations: Station[];
  artists: Artist[];
};
export type Note = {
  id: string;
  recording_id: string;
  time_ms: number;
  text: string;
  created_at: string;
  updated_at: string;
};
export type Localization = {
  status: string;
  error?: string;
  episodes: Record<
    string,
    { title: string; description: string; automatic: boolean; language: string }
  >;
};
export type SearchResult = {
  kind: string;
  id: string;
  episode_id?: string;
  title: string;
  text: string;
  display_text?: string;
  lang?: string;
  display_lang?: string;
  start_ms: number | null;
};
export async function request<T>(
  url: string,
  method = "GET",
  body?: unknown,
  signal?: AbortSignal,
): Promise<T> {
  const res = await fetch("/api/app" + url, {
    method,
    credentials: "same-origin",
    signal,
    headers:
      body === undefined ? undefined : { "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(
      typeof data.detail === "string"
        ? data.detail
        : `Request failed (${res.status}).`,
    );
  }
  return res.json();
}
export const appApi = {
  catalogue: () => request<Catalogue>("/catalogue"),
  me: () => request<User>("/auth/me"),
  auth: (mode: string, username: string, password: string) =>
    request<User>("/auth/" + mode, "POST", { username, password }),
  logout: () => request("/auth/session", "DELETE"),
  preferences: (body: Preferences) =>
    request<User>("/preferences", "PATCH", body),
  notes: () => request<Note[]>("/notes"),
  addNote: (recording_id: string, time_ms: number, text: string) =>
    request<Note>("/notes", "POST", { recording_id, time_ms, text }),
  editNote: (id: string, text: string) =>
    request<Note>("/notes/" + id, "PATCH", { text }),
  deleteNote: (id: string) => request("/notes/" + id, "DELETE"),
  search: (q: string, lang: string, offset: number, signal: AbortSignal) =>
    request<{ results: SearchResult[]; total: number }>(
      `/search?q=${encodeURIComponent(q)}&lang=${lang}&offset=${offset}`,
      "GET",
      undefined,
      signal,
    ),
  localization: (lang: string, start = false) =>
    request<Localization>("/localization/" + lang, start ? "POST" : "GET"),
};
export function country(code: string | null, lang = "en") {
  try {
    return (
      new Intl.DisplayNames([lang === "original" ? "en" : lang], {
        type: "region",
      }).of(code || "") || code
    );
  } catch {
    return code || "";
  }
}
export function language(code: string, lang = "en") {
  try {
    return (
      new Intl.DisplayNames([lang === "original" ? "en" : lang], {
        type: "language",
      }).of(code) || code
    );
  } catch {
    return code;
  }
}
export function date(iso: string | null | undefined, lang = "en") {
  return iso
    ? new Date(iso.slice(0, 10) + "T12:00:00").toLocaleDateString(
        lang === "original" ? "en" : lang,
        { day: "numeric", month: "short", year: "numeric" },
      )
    : "—";
}
export function route(kind: string, id: string, ms?: number | null) {
  return `#/${kind}/${encodeURIComponent(id)}${ms != null ? "?t=" + Math.round(ms) : ""}`;
}
